import {
  Component,
  OnInit,
  signal,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import {
  AssetAvailabilityStatus,
  Customer,
  EquipmentAsset,
} from 'src/app/core/models';
import {
  DefaultsService,
  AuthenticationService,
  EquipmentService,
  ProfileService,
} from 'src/app/core/providers';
import { NavService } from 'src/app/core/providers/services/nav.service';
import { SearchService } from 'src/app/core/providers/services/search.service';
import { ComponentBase } from '../component.base';
import utils from './equipment-asset-utils';
import { FiltersService } from '../../filters/filters.service';

@Component({
  selector: 'app-equipment-asset',
  templateUrl: './equipment-asset.component.html',
  styleUrls: ['./equipment-asset.component.scss'],
  standalone: false,
})
export class EquipmentAssetComponent extends ComponentBase implements OnInit {
  @ViewChild('scrollContainer', { static: false })
  scrollContainer?: ElementRef<HTMLDivElement>;

  hasProductTypeAssetSubscription = false;
  public equipmentAssets: EquipmentAsset[] = [];
  productTypeId: number;
  categoryId: number;
  statusFilter?: string = undefined;
  unavailableStatus: string | null = null;
  isTest = false;
  protected page = 1;
  protected pageSize = location.origin.includes('localhost') ? 100 : 1000;

  public isLoadingMore = false;

  public maxDaysInStatus = signal(90);

  constructor(
    public defaultsService: DefaultsService,
    public authenticationService: AuthenticationService,
    public equipmentService: EquipmentService,
    public profileService: ProfileService,
    public searchService: SearchService,
    public navService: NavService,
    public translateService: TranslateService,
    public route: ActivatedRoute,
    public router: Router,
    public filtersService: FiltersService
  ) {
    super(
      defaultsService,
      authenticationService,
      equipmentService,
      profileService,
      searchService,
      navService,
      translateService,
      route,
      router,
      filtersService
    );

    if (localStorage.getItem('TEST_PAGE_SIZE')) {
      const flagPageSize = localStorage.getItem('TEST_PAGE_SIZE') as string;
      this.pageSize = parseInt(flagPageSize);
    }
  }

  ngOnInit(): void {
    this.authenticationService.startSentryTransaction(
      this.router.routerState.snapshot.url
    );
    const categoryId = this.route.snapshot.paramMap.get('categoryId');
    const productTypeId = this.route.snapshot.paramMap.get('productTypeId');
    const statusFilter = this.route.snapshot.paramMap.get('statusFilter');
    this.unavailableStatus =
      this.route.snapshot.paramMap.get('unavailableStatus');
    // Why is this necessary? Can't we just leave it string or null?
    this.statusFilter = statusFilter ? statusFilter : undefined;

    const maxDays =
      this.authenticationService.userInfoView.currentSelectedClient()
        ?.MaxNumDaysInStatus || 90;

    this.maxDaysInStatus.set(maxDays);

    if (!categoryId || !productTypeId) {
      this.router.navigate(['/categories']);
    } else {
      this.subscriptions.push(
        this.profileService.selectedCustomersChanged.subscribe(
          // eslint-disable-next-line no-unused-vars
          (r: Array<Customer>) => {
            this.loadEquipmentAssets();
          }
        )
      );
      this.categoryId = +categoryId;
      this.productTypeId = +productTypeId;
      this.loadEquipmentAssets();
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s: Subscription) => {
      s.unsubscribe();
    });
  }

  onScrollDown() {
    this.page += 1;
    this.loadEquipmentAssets();
  }

  getAssetLocation(asset: EquipmentAsset): string {
    return utils.getAssetLocation(asset);
  }

  loadEquipmentAssets = () => {
    if (!this.isTest) {
      if (!this.hasProductTypeAssetSubscription) {
        this.subscriptions.push(
          this.equipmentService.equipmentAssetsLoaded.subscribe(
            (newEquipmentAssets) => {
              this.equipmentAssets = [
                ...this.equipmentAssets,
                ...newEquipmentAssets,
              ];
              this.isBusy = false;
              this.isLoadingMore = false;
              this.authenticationService.finishSentryTransaction();
            }
          )
        );
        this.hasProductTypeAssetSubscription = true;
      }

      // Show main spinner for first page, loading indicator for subsequent pages
      if (this.page === 1) {
        this.isBusy = true;
      } else {
        this.isLoadingMore = true;
      }

      this.equipmentService.getProductTypeAssets(
        this.categoryId,
        this.productTypeId,
        this.statusFilter,
        this.unavailableStatus,
        this.page,
        this.pageSize,
        this.getQueryParams()
      );
    } else if (this.equipmentService.equipmentAssets) {
      this.equipmentAssets = this.equipmentService.equipmentAssets;
      this.isBusy = false;
      this.authenticationService.finishSentryTransaction();
    }
  };

  /**
   * This is used by the geography levels links to help determine the correct assets query on the
   * preceding page.
   */
  getQueryParams(): any {
    const queryString = window.location.search;
    const params = new URLSearchParams(queryString);
    if (params.has('geography')) {
      // company options will have no ids
      const ids = params.has('ids')
        ? params
            .get('ids')
            ?.split(',')
            .map((id) => parseInt(id, 10))
        : null;

      return {
        geography: params.get('geography'),
        ids,
      };
    } else {
      return {};
    }
  }

  getDaysInSameStatus = (daysInSameStatus: number | undefined): string => {
    const maxDays = this.maxDaysInStatus();
    if (daysInSameStatus !== undefined) {
      if (daysInSameStatus > maxDays) {
        return `(${this.translateService.instant('main.labels.days_greater_than', { count: maxDays })})`;
      } else if (daysInSameStatus === 1) {
        return `(${this.translateService.instant('main.labels.1_Day')})`;
      } else {
        return `(${this.getLabelTranslation('days_in_status', [daysInSameStatus.toString()])})`;
      }
    }
    return '';
  };
}
